import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {ProvisionLiabilityStatus} from '../../../../../enum/provision-liability-status.enum';
import {ProvisionTypeComponentDto} from '../../../../../model/dto/entity/provision-type-component.dto';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';
import {buildCpWidgetForm} from '../../../../../service/domain/cp-widget-column';
import {
    CP_AGENT_RATE_BASIS_FIELD_ID,
    CP_AGENT_RATE_REASON_FIELD_ID,
    cpAgentRateBasis,
    cpAgentRateReason
} from '../../../../../service/domain/cp-agent-rate-explanation';
import {CpAgentRateBasis} from '../../../../../interface/cp-agent-rate-basis.interface';
import {CpAgentRateReason} from '../../../../../interface/cp-agent-rate-reason.interface';

/**
 * navItem "Verträge / Haftungen" of the ProvisionAgent OBC (04-frontend.md §7), widget alias
 * `cp.agent.contractAgents.table`.
 *
 * ── WHAT THIS PANE LISTS, AND WHY IT CHANGED (WP-E14)
 *
 * It lists the PROVISIONSSÄTZE of this Abrechnungskonto — `cp.ProvisionContractAgent` (R-08), one
 * row per (Ereignis × Provisionsart × Berater). Its predecessor listed `cp.ProvisionContract`
 * (R-07) instead, which stopped being the right level in WP-E5: the agent, the amount, the matrix
 * result and every liability field moved OFF the contract event onto the rate row (A6-04), so a
 * `cp.ProvisionContract` row no longer carries a `provisionAgentId` at all and could not even be
 * filtered by this pane's own agent any more.
 *
 * The change also fixes the level the three actions write on. All three endpoints of 04 §7 address
 * a RATE row — `POST /mvsa/cp/provisionContractAgents/{id}/…` — and §7 states in as many words that
 * the agent change "muss den Satz auswählen lassen (Vertrag × Ereignis × Provisionsart)". Selecting
 * a row of THIS table is that selection; no extra picker is needed, and none may be added, because
 * an id chosen anywhere else could not be shown next to the amounts it is about to change.
 *
 * ── THE THREE ACTIONS (04 §7, all under um right `cp_agentChange` — AX-08 adds no new right)
 *
 *   "Betrag überschreiben"  -> POST /{id}/overwriteAmount   ({@link CpProvisionAgentAmountOverwriteDialogComponent})
 *   "Berater wechseln"      -> POST /{id}/changeAgent       ({@link CpProvisionAgentChangeDialogComponent})
 *   "Satz manuell anlegen"  -> the top-down create wizard, see below
 *
 * "Satz manuell anlegen" IS REWIRED (user decision). The button keeps its place and its label, but
 * opens {@code <cp-provision-contract-create-wizard>} with the Provisionszeile of the SELECTED row
 * as `provisionContractId`, instead of {@link CpProvisionAgentManualRateDialogComponent}. The old
 * dialog is verifiably unable to create a row — it posts three fields where Bean Validation demands
 * eight mandatory ones plus three denormalisations — and since it builds its form by hand instead of
 * from metadata, repairing it would amount to rebuilding the wizard's capture step. The dialog
 * COMPONENT is left untouched; only this call site moved.
 *
 * Every one of them is a TRANSIENT FORM WIDGET built from an `MvsFormDto` (design gate finding B-9;
 * repo evidence `wf-process-start.component.ts`). This component contains NO form HTML.
 *
 * ── PERMISSION (U-08)
 *
 * {@link canAgentChange} mirrors the um feature right `cp_agentChange` and defaults to TRUE
 * (optimistic) — the module-wide pattern, reasoned once in {@link CpFeatureRight}: this frontend
 * has no feature-rights service and no cp payload carries a grant, so a `false` default would never
 * be raised and all three buttons would be permanently inert. The boundary is
 * `cp/cross/um/ProvisionFeatureGuard` (itself default-ALLOW), and a real refusal arrives as HTTP
 * 403, which demotes the flag through {@link handleAgentChangeForbidden}.
 *
 * ── LAYOUT: REVIEW WORKBENCH (skill fe_core_review-workbench)
 *
 * The pane is a two-pane Review Workbench IN THE FULL VIEW; the side view runs it list-only via
 * {@link allowDetailPane} (see there for why). LEFT (persistent) is everything this pane always had —
 * the two filters, the three action buttons, the selection line and the QL table with its six joins
 * and the fixed filter `e.provisionAgentId`. RIGHT is the selected Provisionssatz in object mode,
 * `<mvs-object [objectType]="'cp.ProvisionContractAgent'">`, which the registry resolves to
 * CpProvisionContractAgentObjectComponent. Without a selection the workbench runs in 1-pane mode.
 *
 * R2/R3 of the skill are what the structure is FOR: `[panes]` toggles between 1 and 2 on the SAME
 * splitter, and only the CONTENT of the detail pane is conditional. An `@if` around the pane would
 * make PrimeNG rebuild the splitter and take the projected table with it — losing sort order,
 * filters, scroll position and selection on every single click.
 *
 * HEIGHT — FD-4, and the reason this instance looks under-configured: opened through
 * `/#/cp/dashboard/agents` this pane already sits inside the detail pane of an OUTER workbench, so
 * this is a LEVEL-2 instance. It binds NEITHER `autoFitViewport` NOR `viewportOffsetPx` and falls to
 * `height: 100%`. Auto-fit measures `clientHeight - getBoundingClientRect().top - bottomGap` with no
 * container awareness, so nested it would size to the WINDOW bottom, overshoot its pane, and have the
 * overshoot silently clipped by `.p-splitter-panel { overflow: hidden }` — stable, flicker-free and
 * therefore invisible to a review. The height chain that makes the 100% resolve is documented in
 * cp-provision-agent-object.component.scss and cp-provision-agents.component.scss.
 *
 * ── THE TWO WIZARDS (ADDITIVE — user decision O-4)
 *
 *   "Provisionszeile anlegen"    -> {@code <cp-provision-contract-create-wizard>}
 *   "Provisionssatz anpassen"    -> {@code <cp-provision-contract-adjust-wizard>}
 *
 * Both are DIALOGS, not routes, and their host contract is deliberately the same as the three
 * existing action dialogs above — `[(visible)]`, `[canAgentChange]`, `(onForbidden)`. They come IN
 * ADDITION to those three; not one existing dialog or button is replaced or removed.
 *
 * ── REFRESH
 *
 * Rate rows are run output: every run rewrites amounts, reserve targets and liability state. The
 * pane therefore subscribes to `status$` of the global job indicator and reloads IN PLACE on the
 * running -> not-running transition, and additionally on an `objectChangeEvent` for
 * `cp.ProvisionContractAgent` so its own writes show up at once.
 */
@Component({
    selector: 'cp-provision-agent-contract-agents',
    templateUrl: './cp-provision-agent-contract-agents.component.html',
    styleUrls: ['./cp-provision-agent-contract-agents.component.scss'],
    standalone: false
})
export class CpProvisionAgentContractAgentsComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    /** um right `cp_agentChange` (U-08) — optimistic default, demoted on 403; see class Javadoc. */
    @Input() canAgentChange: boolean = true;

    /** Display name of the Abrechnungskonto; passed as context into the dialogs. */
    @Input() provisionAgentName: string;

    /**
     * May this pane offer the detail pane, i.e. run as a TWO-pane workbench?
     *
     * <p>TRUE for the full view. FALSE for the side view, which reaches the user through the object
     * navigation sidebar — a {@code p-drawer}. A worklist with a filter bar and thirteen columns
     * next to a complete Provisionssatz OBC, under minimum pane widths of 30 % and 25 %, is not
     * operable at drawer width, and the side view keeps the heavy lists reserved for the full view
     * by its own design ({@code CpProvisionAgentSideComponent}).
     *
     * <p>Nothing is lost: the drawer's route to a detail view is "im neuen Fenster öffnen", which
     * switches the OBC to {@code uiMode = 'full'} and brings the workbench with it.
     *
     * <p>It gates BOTH the {@code panes} count AND the instantiation of the detail content — a
     * collapsed pane still builds what is inside it, so without the second guard the side view would
     * silently mount an {@code <mvs-object>} and fetch a whole OBC that nobody can see.
     */
    @Input() allowDetailPane: boolean = true;

    contractAgentsWidget: WidgetData;

    // --- Filters (04 §7: "Filter auf Provisionsart und Haftungsstatus") ----------------------- //

    /** Selected Provisionsart (`cp.ProvisionTypeComponent`); null = all. */
    filterProvisionTypeComponentId: number = null;

    /** Selected Haftungsstatus; null = all. */
    filterLiabilityStatus: ProvisionLiabilityStatus = null;

    /** Options of the Provisionsart filter — loaded once, active components only. */
    provisionTypeComponentOptions: { label: string, value: number }[] = [{label: 'Alle Provisionsarten', value: null}];

    /** fieldIds of the two `#cust_` columns (Basis, Grund) — bound in the template. */
    readonly rateBasisFieldId: string = CP_AGENT_RATE_BASIS_FIELD_ID;
    readonly rateReasonFieldId: string = CP_AGENT_RATE_REASON_FIELD_ID;

    readonly liabilityStatusOptions: { label: string, value: ProvisionLiabilityStatus }[] = [
        {label: 'Alle Haftungsstatus', value: null},
        {label: 'Aktiv', value: ProvisionLiabilityStatus.ACTIVE},
        {label: 'Abgelaufen', value: ProvisionLiabilityStatus.EXPIRED},
        {label: 'Storniert', value: ProvisionLiabilityStatus.CANCELLED},
        {label: 'Beitragsfrei', value: ProvisionLiabilityStatus.SUSPENDED}
    ];

    // --- Selection + dialog state ------------------------------------------------------------ //

    /** The selected Provisionssatz; null = no selection, all three actions locked. */
    selectedProvisionContractAgentId: number = null;

    /** Display text of the selection (Vertrag · Ereignis · Provisionsart), purely informational. */
    selectedProvisionContractAgentLabel: string = null;

    /**
     * The Provisionszeile (`cp.ProvisionContract`) the selected Satz belongs to, or null when the row
     * did not carry it.
     *
     * Read off the selected row rather than fetched: the adjust wizard treats a null
     * `provisionContractId` as "this run can only adjust the Provisionssatz", which is exactly the
     * honest state when the id is not on the row. Nothing here may fail because of a missing key.
     */
    selectedProvisionContractId: number = null;

    showAgentChangeDialog: boolean = false;
    showAmountOverwriteDialog: boolean = false;

    /**
     * The create wizard, shared by BOTH of its entry points — one instance, two meanings, told apart
     * by {@link createWizardProvisionContractId} alone.
     */
    showContractCreateWizard: boolean = false;

    /**
     * The Provisionszeile the create wizard runs against, or null to let it create one.
     *
     * This single field IS the difference between the two entries, so both open methods set it
     * explicitly rather than relying on whatever a previous run left behind:
     *   "Provisionszeile anlegen"   -> null    : the wizard creates the Zeile and its Sätze top-down
     *   "Satz manuell anlegen"      -> the row : step 1 runs as a read-only context display and only
     *                                           the Sätze are captured
     * Getting this wrong is not a cosmetic bug — a stray null would turn "Satz zu dieser Zeile
     * ergänzen" into "neue Zeile anlegen" and walk the user into the time-slice refusal of
     * ProvisionContractOnChange.assertUniqueEventKey.
     */
    createWizardProvisionContractId: number = null;

    /** "Provisionssatz anpassen" — the top-down adjust wizard on the selected row. */
    showContractAdjustWizard: boolean = false;

    /** Is a cp job currently running? Feeds the in-progress hint above the table. */
    jobRunning: boolean;

    /**
     * The mounted widget instance — it exists only for the in-place reload after a job.
     * `refreshWidget()` reloads WITHOUT touching the binding; a freshly re-assigned
     * `contractAgentsWidget` would lose sorting, scroll position, and selection.
     */
    @ViewChild('contractAgentsWidgetRef') contractAgentsWidgetComponent: MvsWidgetComponent;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;
    protected objectChangeSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToJobCompletion();
        this.subscribeToObjectChanges();
        this.loadProvisionTypeComponents();
        this.refreshComponent();
    }

    /**
     * Load the Provisionsart filter options once.
     *
     * A generic FK filter field cannot be used here: the options must be restricted to ACTIVE
     * components and the "Alle Provisionsarten" entry has no entity behind it. The sibling dialogs
     * of this OBC build their value lists the same way.
     */
    protected loadProvisionTypeComponents(): void {

        const filterCriteria = [
            FilterCriteria.create('entityStatus', FilterCriteria.cOperatorEqual, EntityStatusEnum.active)
        ];

        this.coreService.getCrudService('cp.ProvisionTypeComponent')
            .list(ObjectRequestList.createBasic(false, filterCriteria, null))
            .subscribe({
                next: (dtoList) => {
                    const entries = (dtoList?.entries as ProvisionTypeComponentDto[]) ?? [];
                    this.provisionTypeComponentOptions = [
                        {label: 'Alle Provisionsarten', value: null},
                        ...entries.map(entry => ({label: entry.name, value: entry.id}))
                    ];
                },
                error: () => {
                    // A failed option load must not take the table down with it — the filter simply
                    // stays at "Alle Provisionsarten".
                    this.provisionTypeComponentOptions = [{label: 'Alle Provisionsarten', value: null}];
                }
            });
    }

    /**
     * Reload on the running -> not-running transition (04-frontend.md §3). Rate rows are run
     * results; while a run is in progress the table shows an intermediate state.
     */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.reloadInPlace();
            }
        });
    }

    /** The pane's own writes (and any other rate change) reload the list. */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionContractAgent') {
                this.reloadInPlace();
            }
        });
    }

    /** In-place reload of the mounted table; falls back to a full rebuild if none is mounted. */
    protected reloadInPlace(): void {
        if (this.contractAgentsWidgetComponent) {
            this.contractAgentsWidgetComponent.refreshWidget();
            return;
        }
        this.refreshComponent();
    }

    /**
     * (Re)build the QL table with the current filter state.
     *
     * `contract`, `provisionType` and `provisionTypeContractEvent` are denormalised onto R-08
     * (A6-04) for exactly this join-free selection path; the join on `provisionContract` is here
     * only for the event period, which lives on R-07 alone.
     */
    refreshComponent(): void {

        if (!this.provisionAgentId) {
            this.contractAgentsWidget = null;
            this.handleClearSelection();
            this.initialized = true;
            return;
        }

        const fixedFilters: FilterCriteria[] = [
            FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId)
        ];

        if (this.filterProvisionTypeComponentId !== null && this.filterProvisionTypeComponentId !== undefined) {
            fixedFilters.push(FilterCriteria.create('e.provisionTypeComponentId',
                FilterCriteria.cOperatorEqual, this.filterProvisionTypeComponentId));
        }

        if (this.filterLiabilityStatus !== null && this.filterLiabilityStatus !== undefined) {
            fixedFilters.push(FilterCriteria.create('e.liabilityStatus',
                FilterCriteria.cOperatorEqual, this.filterLiabilityStatus));
        }

        const qlQuery: QlQueryDto = {
            name: 'cp.agent.contractAgents',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractAgent',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'contract', as: 'c', joinType: 'left',
                        fields: [{name: 'contractIdentifier'}, {name: 'alias'}]
                    },
                    {
                        type: 'entity', name: 'provisionContract', as: 'pc', joinType: 'left',
                        fields: [{name: 'startDate'}, {name: 'endDate'}]
                    },
                    {
                        type: 'entity', name: 'provisionTypeContractEvent', as: 'pce', joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity', name: 'provisionTypeComponent', as: 'ptc', joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity', name: 'provisionType', as: 'pt', joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity', name: 'liabilityType', as: 'lt', joinType: 'left',
                        fields: [{name: 'name'}]
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            fixedFilters: fixedFilters,
            sortings: [
                new Sorting('pc.startDate', false),
                new Sorting('e.id', false)
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Table of the Provisionssätze (cp.ProvisionContractAgent) of one Abrechnungskonto -
         *         Provisionsart, Ereignis and its period, Basis x Faktor = Betrag, the overwrite
         *         with the reason why a row pays nothing or an overwritten amount (M-026, factor
         *         0, base 0), liability type, liability status and the amount still in
         *         liability. Row selection is the Satz selection the three §7 action dialogs write on.
         */
        this.contractAgentsWidget = WidgetFactory.createWidgetTableQl(
            'cp.agent.contractAgents.table',
            'Provisionssätze',
            qlQuery,
            WidgetDataParam.create('paging', 25),
            WidgetDataParam.create('size', 'S'),
            // Worklist of a Review Workbench: one row at a time drives the detail pane, so the table
            // offers an explicit per-row select button and emits (onObjectSelect) from it.
            WidgetDataParam.create('selectionMode', 'single'),
            // Reading order „wofür -> wie gerechnet -> was kommt raus -> warum": Basis x Faktor =
            // Betrag stand as adjacent columns, so a 276,99 from a 39,57 base is explained by the
            // factor 7 in the same row instead of only in the Satz workbench. „Basis" is a #cust_
            // column because the factor applies to a DIFFERENT base per mode (monthly for
            // MONTHLY_PREMIUM, total-term for PER_MILLE). „Grund" says why a row pays nothing or
            // an overwritten amount (M-026 reason, factor 0, base 0) — the open question of every
            // zero row. Overwrite source and liability source moved to the Satz detail; the
            // vocabulary is „Übersteuerung", as in the detail form.
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'c.contractIdentifier', label: 'Vertrag'},
                {field: 'ptc.name', label: 'Provisionsart'},
                {field: 'pce.name', label: 'Ereignis'},
                {field: 'pc.startDate', label: 'Periode ab'},
                {field: 'pc.endDate', label: 'Periode bis'},
                {field: '#cust_' + CP_AGENT_RATE_BASIS_FIELD_ID, label: 'Basis'},
                {field: 'e.appliedFactor', label: 'Faktor'},
                {field: 'e.provisionAmount', label: 'Betrag'},
                {field: 'e.provisionAmountOverwrite', label: 'Übersteuerung'},
                {field: '#cust_' + CP_AGENT_RATE_REASON_FIELD_ID, label: 'Grund / Meldung'},
                {field: 'lt.name', label: 'Haftungstyp'},
                {field: 'e.liabilityStatus', label: 'Haftungsstatus'},
                {field: 'e.inLiabilityAmount', label: 'In Haftung'},
                {field: 'e.source', label: 'Quelle'}
            ]))
        );

        this.initialized = true;
    }

    /** The base the factor of a row was applied to (custom column „Basis"). */
    rateBasisOf(object: any): CpAgentRateBasis {
        return cpAgentRateBasis(object);
    }

    /** Why a row pays nothing or an overwritten amount (custom column „Grund / Meldung"). */
    rateReasonOf(object: any): CpAgentRateReason | null {
        return cpAgentRateReason(object);
    }

    /** Filter change — rebuild the table and drop a selection that may no longer be visible. */
    handleFilterChange(): void {
        this.handleClearSelection();
        this.refreshComponent();
    }

    /** Reset both filters. */
    handleFilterReset(): void {
        this.filterProvisionTypeComponentId = null;
        this.filterLiabilityStatus = null;
        this.handleFilterChange();
    }

    /**
     * Row selection -> unlocks the three actions.
     *
     * An unreadable payload does NOT clear the selection: the table emits `onObjectSelect` only on
     * a row click and has no deselection, so a missing id is an error case — the existing selection
     * must not disappear because of it.
     */
    handleContractAgentSelect(object: ObjectIdentifier | ObjectIdentifierData | any): void {

        const objectId = object?.objectId ?? object?.id ?? object?.data?.['e.id'];

        if (!objectId) {
            return;
        }

        this.selectedProvisionContractAgentId = Number(objectId);
        this.selectedProvisionContractAgentLabel = this.readContractAgentLabel(object);
        this.selectedProvisionContractId = this.readProvisionContractId(object);
    }

    /**
     * The Provisionszeile id of the selected row, or null when the row does not carry it.
     *
     * Same defensive read as {@link readContractAgentLabel}: the column keys of a QL row depend on
     * alias and resolution, and this id is an OPTIONAL input of the adjust wizard — a missing key
     * narrows what that run can adjust, it must never break the selection.
     */
    protected readProvisionContractId(object: any): number {

        const data = object?.data ?? object?.objectData;

        const raw = data?.['e.provisionContractId'] ?? data?.['provisionContractId'] ?? data?.['pc.id'];

        return raw ? Number(raw) : null;
    }

    /**
     * Display text of the selected row: Vertrag · Ereignis · Provisionsart — the triple 04 §7 names
     * as the identity of a Satz.
     *
     * The column keys of a QL row depend on alias and resolution, so several plausible keys are
     * tried and the id is the last resort. This text is pure information in the dialog headers — it
     * must never be the reason a selection fails.
     */
    protected readContractAgentLabel(object: any): string {

        const data = object?.data ?? object?.objectData;

        const contractName = data?.['c.contractIdentifier'] ?? data?.['e.contractDtoName'] ?? data?.['contractDtoName'];
        const eventName = data?.['pce.name'] ?? data?.['e.provisionTypeContractEventDtoName'];
        const componentName = data?.['ptc.name'] ?? data?.['e.provisionTypeComponentDtoName'];

        const parts = [contractName, eventName, componentName].filter(part => !!part);

        return parts.length ? parts.join(' · ') : `#${this.selectedProvisionContractAgentId}`;
    }

    /**
     * Clear the selection — the row actions lock again AND the workbench falls back to 1-pane mode,
     * which is what takes the detail `<mvs-object>` out of the DOM (its content is conditional).
     */
    handleClearSelection(): void {
        this.selectedProvisionContractAgentId = null;
        this.selectedProvisionContractAgentLabel = null;
        this.selectedProvisionContractId = null;
    }

    /**
     * Why a row action is locked, in German — a button never sits dead without a reason.
     *
     * The two causes must not be collapsed into one text: a server refusal is something the
     * caseworker cannot fix, a missing selection is something they fix in one click.
     *
     * @param grantedText the tooltip to show when the action is available
     */
    protected rowActionTooltip(grantedText: string): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (!this.selectedProvisionContractAgentId) {
            return 'Wählen Sie zuerst einen Provisionssatz aus.';
        }

        return grantedText;
    }

    get agentChangeTooltip(): string {
        return this.rowActionTooltip('Berater des ausgewählten Provisionssatzes wechseln.');
    }

    get amountOverwriteTooltip(): string {
        return this.rowActionTooltip('Betrag des ausgewählten Provisionssatzes überschreiben.');
    }

    /**
     * Why "Satz manuell anlegen" is locked. Three distinct causes, three distinct texts — the third
     * one is not the user's fault and must not read like the second.
     */
    get manualRateTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (!this.selectedProvisionContractAgentId) {
            return 'Wählen Sie zuerst einen Provisionssatz aus, dessen Provisionszeile ergänzt werden soll.';
        }

        if (!this.selectedProvisionContractId) {
            return 'Zu dem ausgewählten Provisionssatz konnte keine Provisionszeile ermittelt werden. '
                + 'Nutzen Sie „Provisionszeile anlegen“, um eine neue Provisionszeile anzulegen.';
        }

        return 'Weiteren Provisionssatz zur Provisionszeile des ausgewählten Satzes anlegen.';
    }

    /** True while a row action may be offered — right granted AND a Satz selected. */
    get rowActionAvailable(): boolean {
        return this.canAgentChange && !!this.selectedProvisionContractAgentId;
    }

    openAgentChangeDialog(): void {
        if (!this.rowActionAvailable) {
            return;
        }
        this.showAgentChangeDialog = true;
    }

    openAmountOverwriteDialog(): void {
        if (!this.rowActionAvailable) {
            return;
        }
        this.showAmountOverwriteDialog = true;
    }

    /**
     * "Satz manuell anlegen" — rewired to the create wizard, running against the Provisionszeile of
     * the SELECTED row.
     *
     * It therefore needs a selection now, which the old dialog did not: that dialog carried its own
     * mandatory "Ereigniszeile" FK picker, so the user chose the parent inside it. The parent now
     * comes from the table row instead. See {@link manualRateAvailable} for why the guard is on the
     * ZEILE and not merely on the row.
     */
    openManualRateWizard(): void {
        if (!this.manualRateAvailable) {
            return;
        }
        this.createWizardProvisionContractId = this.selectedProvisionContractId;
        this.showContractCreateWizard = true;
    }

    /**
     * True while "Satz manuell anlegen" may be offered — right granted AND the selected row actually
     * yielded its Provisionszeile.
     *
     * Deliberately NOT {@link rowActionAvailable}: that one only asks whether a row is selected. A
     * row whose `provisionContractId` could not be read would pass it and hand the wizard a null
     * parent, which silently turns the action into "neue Zeile anlegen". Better a disabled button
     * with a reason than a run that quietly does something else.
     */
    get manualRateAvailable(): boolean {
        return this.canAgentChange && !!this.selectedProvisionContractId;
    }

    /**
     * "Provisionszeile anlegen" — the top-down create wizard. It CREATES, so like the manual-rate
     * dialog it needs no selection, only the right.
     */
    openContractCreateWizard(): void {
        if (!this.canAgentChange) {
            return;
        }
        // Explicit null, not "leave it alone": this entry creates a NEW Provisionszeile, and a value
        // left over from a previous "Satz manuell anlegen" run would silently change what it does.
        this.createWizardProvisionContractId = null;
        this.showContractCreateWizard = true;
    }

    /**
     * "Provisionssatz anpassen" — the top-down adjust wizard. It works on the SELECTED Satz (and,
     * when the row carried it, on its Provisionszeile), so it needs both the right and a selection.
     */
    openContractAdjustWizard(): void {
        if (!this.rowActionAvailable) {
            return;
        }
        this.showContractAdjustWizard = true;
    }

    get contractCreateTooltip(): string {
        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }
        return 'Provisionszeile und ihre Provisionssätze in einem Durchlauf anlegen.';
    }

    get contractAdjustTooltip(): string {
        return this.rowActionTooltip(
            'Ausgewählten Provisionssatz und seine Provisionszeile in einem Durchlauf anpassen.');
    }

    /**
     * A wizard finished. The write path is generic CRUD, so `objectChangeEvent` reloads the table
     * anyway; the explicit reload covers the create run, whose new Sätze may not be announced under
     * this pane's own object type.
     */
    handleWizardCompleted(): void {
        this.handleClearSelection();
        this.reloadInPlace();
    }

    /**
     * A dialog saw a 403 — the um right `cp_agentChange` answering the hard way.
     *
     * Demote the pane's flag too, so the entry buttons go quiet instead of reopening a dialog the
     * server will refuse again. Sticky for this component instance; navigating away and back builds
     * a fresh instance that probes again, which is correct — the grant may have been seeded since.
     */
    handleAgentChangeForbidden(): void {
        this.canAgentChange = false;
    }

    /** A write went through — the list reloads via `objectChangeEvent` anyway. */
    handleContractAgentChanged(): void {
        this.handleClearSelection();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.handleClearSelection();
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
        this.objectChangeSubscription?.unsubscribe();
    }

}
